import { useMemo, useState } from "react";
import { number, object } from "prop-types";
import { observer } from "mobx-react";
import { WorkTypeValueState } from "@repo/utils";
import { Dialog } from "@components";
import { IconButton } from "@material-ui/core";
import { Percent, Settings } from "@assets";
import { useEstimateStructure } from "@tools";
import { CellFormatSelect } from "./components/CellFormatSelect";
import { CellSettingsTitle } from "./components/CellSettingsTitle";
import { CellAutomation } from "./components/CellAutomation";
import classnames from "classnames";

export const TaskWorkTypeCellSettings = observer(({
  element,
  workType,
  tableDepth,
}) => {
  const structure = useEstimateStructure();
  const { useMinMax } = structure.settings;
  
  const [ visible, show ] = useState(false);

  const lighterColor = useMemo(() => (
    tableDepth === 0 ? "color-primary-lighter" : undefined
  ), [tableDepth]);
  
  const forcedState = element.getWorkTypeForcedState(workType.id) || (useMinMax ? WorkTypeValueState.MIN_MAX : WorkTypeValueState.SINGLE);
  
  const hasAutomation = element.hasWorkloadCellAutomation(workType.id);
  const hasOwnAutomation = element.hasOwnWorkloadCellAutomation(workType.id);
  
  const handlePopup = () => show(!visible);
  
  const handleVariantChange = (e, v) => {
    element.setWorkTypeForcedState(workType.id, v, true);
  }
  
  const handleAutomationChange = ({ percent, usedWorkTypes, usedSections }) => {
    element.setWorkTypeAutomation(workType.id, percent, usedWorkTypes, usedSections);
    show(false);
  }
  
  return <>
    {
      hasAutomation && !element.isValueOverridden(workType.id) &&
      <IconButton
        onClick={ hasOwnAutomation ? handlePopup : undefined }
        color={ hasOwnAutomation ? "primary" : "secondary" }
        size="small"
        className={classnames("noDrag p-0-5 text-sm", !hasOwnAutomation && "expand text-lighter", lighterColor )}
        tabIndex={-1}
      >
        <Percent />
      </IconButton>
    }
    <IconButton
      onClick={handlePopup}
      color="primary"
      size="small"
      className={classnames("noDrag p-0-5 text-sm cellHoverIcon nonDisplay", lighterColor) }
      tabIndex={-1}
    >
      <Settings />
    </IconButton>
    {
      visible &&
      <Dialog
        open={visible}
        onClose={handlePopup}
        width={720}
        title={<CellSettingsTitle element={element} workType={workType} />}
      >
        <CellFormatSelect
          value={forcedState}
          disabled={workType.hasPercent || element.hasWorkloadCellAutomation(workType.id)}
          onChange={handleVariantChange}
        />
        <CellAutomation
          section={element.section}
          workType={workType}
          element={element}
          onSave={handleAutomationChange}
        />
      </Dialog>
    }
  </>
})

TaskWorkTypeCellSettings.propTypes = {
  element: object.isRequired,
  workType: object.isRequired,
  tableDepth: number.isRequired,
}
